import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react"
import { ThumbsDown, ThumbsUp } from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import type { ChatMessage, MessageFeedback } from "@/types/chatHistory"
import { filterHttpSources } from "@/lib/sources"
import { ChatMessageContent } from "@/components/ChatMessageContent"
import { ChatThinking } from "@/components/ChatThinking"
import { SmeFeedbackControl } from "@/components/SmeFeedbackControl"
import { DeepDiveButton } from "@/components/DeepDiveButton"
import {
  CHAT_EMPTY_STATE_HINT,
  CHAT_THINKING_MESSAGE,
  CHAT_WELCOME_DESCRIPTION,
  CHAT_WELCOME_TITLE,
  EXAMPLE_CHAT_PROMPTS,
} from "@/content/exampleChatPrompts"

export type { ChatMessage }

export interface MainContentHandle {
  scrollToBottom(): void
}

interface MainContentProps {
  messages?: ChatMessage[]
  chatId?: string | null
  onFeedback?: (feedback: MessageFeedback, assistantTimestamp?: string) => void
  feedbackDisabled?: boolean
  /** On-demand: generate the deep-dive message for a finalized assistant answer (Thread H). */
  onRequestDeepDive?: (assistantTimestamp?: string) => Promise<boolean> | void
  /** Whether a deep-dive request for the given message is in flight. */
  isRequestingDeepDive?: (assistantTimestamp?: string) => boolean
  /** Latest progress line for an in-flight deep dive (CEO-1). */
  deepDiveStatus?: (assistantTimestamp?: string) => string | undefined
  /** 08-24: try-again message when the last dive attempt for a message failed. */
  deepDiveError?: (assistantTimestamp?: string) => string | undefined
  onExampleMessage?: (message: string) => void
  examplesDisabled?: boolean
}

export const MainContent = forwardRef<MainContentHandle, MainContentProps>(
  function MainContent(
    { messages = [], chatId, onFeedback, feedbackDisabled = false, onRequestDeepDive, isRequestingDeepDive, deepDiveStatus, deepDiveError, onExampleMessage, examplesDisabled = false },
    ref
  ) {
    const viewportRef = useRef<HTMLDivElement>(null)
    // Whether the viewport is currently pinned to (near) the bottom. While
    // pinned we follow new content as it streams in; once the user scrolls up
    // we stop yanking them back down until they return to the bottom.
    const stickToBottomRef = useRef(true)

    const isNearBottom = useCallback(() => {
      const el = viewportRef.current
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight < 64
    }, [])

    useEffect(() => {
      const el = viewportRef.current
      if (!el) return
      const handleScroll = () => {
        stickToBottomRef.current = isNearBottom()
      }
      el.addEventListener("scroll", handleScroll, { passive: true })
      return () => el.removeEventListener("scroll", handleScroll)
    }, [isNearBottom])

    // Auto-scroll as messages grow (e.g. streamed tokens) only while pinned, so
    // a user reading earlier messages isn't dragged down on every update.
    useLayoutEffect(() => {
      if (!stickToBottomRef.current) return
      const el = viewportRef.current
      if (el) el.scrollTop = el.scrollHeight
    }, [messages])

    const lastAssistantIndex = useMemo(() => {
      let idx = -1
      messages.forEach((m, i) => {
        if (m.role === "assistant") idx = i
      })
      return idx
    }, [messages])

    useImperativeHandle(
      ref,
      () => ({
        scrollToBottom() {
          const el = viewportRef.current
          if (!el) return
          // An explicit request (e.g. the user just sent a message) re-pins the
          // viewport so streaming follows the latest content again.
          stickToBottomRef.current = true
          el.scrollTop = el.scrollHeight
        },
      }),
      []
    )

    return (
      <ScrollArea className="flex-1 min-h-0" viewportRef={viewportRef}>
        <div className="p-4 space-y-4">
          {messages.length === 0 ? (
            <div className="mx-auto flex max-w-xl flex-col items-center gap-4 py-8 text-center">
              <div className="space-y-2">
                <h2 className="text-lg font-semibold">{CHAT_WELCOME_TITLE}</h2>
                <p className="text-sm text-muted-foreground">
                  {CHAT_WELCOME_DESCRIPTION}
                </p>
                <p className="text-sm text-muted-foreground">
                  {CHAT_EMPTY_STATE_HINT}
                </p>
              </div>
              <div className="grid w-full max-w-2xl gap-2">
                {EXAMPLE_CHAT_PROMPTS.map((question) => (
                  <Button
                    key={question}
                    type="button"
                    variant="outline"
                    className="h-auto min-h-10 whitespace-normal px-3 py-2.5 text-left text-sm"
                    disabled={examplesDisabled || !onExampleMessage}
                    onClick={() => onExampleMessage?.(question)}
                  >
                    {question}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => {
              const httpSources =
                msg.role === "assistant" ? filterHttpSources(msg.sources) : []
              const showFeedback =
                msg.role === "assistant" &&
                i === lastAssistantIndex &&
                !msg.streaming &&
                Boolean(chatId) &&
                Boolean(onFeedback)
              const showDeepDive =
                msg.role === "assistant" &&
                !msg.streaming &&
                !msg.deep_dive &&
                Boolean(msg.timestamp) &&
                Boolean(chatId) &&
                Boolean(onRequestDeepDive)

              return (
                <div
                  key={msg.timestamp ?? `${i}-${msg.role}`}
                  className={
                    msg.role === "user" ? "flex justify-end" : "flex justify-start"
                  }
                >
                  <div className="flex min-w-0 max-w-[85%] flex-col gap-1">
                    {msg.role === "assistant" && msg.thinking ? (
                      <ChatThinking
                        thinking={msg.thinking}
                        streaming={msg.streaming}
                      />
                    ) : null}
                    <div
                      className={
                        msg.role === "user"
                          ? "rounded-lg bg-primary text-primary-foreground px-3 py-2"
                          : "rounded-lg bg-muted px-3 py-2"
                      }
                    >
                      {msg.role === "assistant" &&
                      msg.streaming &&
                      !msg.content ? (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <span className="chat-loading-text">
                            {(msg.status ?? CHAT_THINKING_MESSAGE).replace(
                              /[\s.…]+$/u,
                              ""
                            )}
                          </span>
                          <span
                            className="inline-flex items-center gap-1"
                            aria-hidden="true"
                          >
                            <span className="chat-loading-dot" />
                            <span className="chat-loading-dot" />
                            <span className="chat-loading-dot" />
                          </span>
                        </div>
                      ) : (
                        <ChatMessageContent content={msg.content} role={msg.role} />
                      )}
                    </div>
                    {/* Post-answer progress: the answer has streamed but the turn
                        is still running a follow-up step. Explains why the user
                        is still waiting. */}
                    {msg.role === "assistant" &&
                    msg.streaming &&
                    msg.content &&
                    msg.status ? (
                      <div className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
                        <span className="chat-loading-text">
                          {msg.status.replace(/[\s.…]+$/u, "")}
                        </span>
                        <span
                          className="inline-flex items-center gap-1"
                          aria-hidden="true"
                        >
                          <span className="chat-loading-dot" />
                          <span className="chat-loading-dot" />
                          <span className="chat-loading-dot" />
                        </span>
                      </div>
                    ) : null}
                    {/* The answer's sources sit directly under the answer bubble — BEFORE
                        the deep dive — so they can't read as the dive's sources. */}
                    {httpSources.length > 0 && (
                      <div className="px-1 pt-1 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">
                          Sources
                        </p>
                        <ul className="space-y-0.5">
                          {httpSources.map((url) => (
                            <li key={url}>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary underline break-all hover:opacity-80"
                              >
                                {url}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Deep dive: a second assistant message bubble (Thread H), styled like
                        the answer, with definitions + detail + reference links. */}
                    {msg.role === "assistant" && msg.deep_dive ? (
                      <div className="mt-1 rounded-lg bg-muted px-3 py-2">
                        {/* allowHtml only when the ST-14 debug section is present, so normal dives keep
                            react-markdown's safe HTML escaping (a stray "<" in statute text won't mangle). */}
                        <ChatMessageContent
                          content={msg.deep_dive}
                          role="assistant"
                          allowHtml={msg.deep_dive.includes("Deep-dive debug")}
                        />
                      </div>
                    ) : null}
                    {/* Action row: feedback thumbs (left) and the on-demand deep dive
                        (right) share one line under the answer. */}
                    {(showFeedback || showDeepDive) && (
                      <div className="flex items-center justify-between gap-1 pt-0.5">
                        <div className="flex items-center gap-1 pl-1">
                          {showFeedback && (
                            <>
                              <Button
                                type="button"
                                variant={msg.feedback === "up" ? "secondary" : "ghost"}
                                size="icon"
                                className="h-8 w-8"
                                disabled={feedbackDisabled}
                                aria-label="Helpful"
                                aria-pressed={msg.feedback === "up"}
                                onClick={() => onFeedback?.("up", msg.timestamp)}
                              >
                                <ThumbsUp className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant={msg.feedback === "down" ? "secondary" : "ghost"}
                                size="icon"
                                className="h-8 w-8"
                                disabled={feedbackDisabled}
                                aria-label="Not helpful"
                                aria-pressed={msg.feedback === "down"}
                                onClick={() => onFeedback?.("down", msg.timestamp)}
                              >
                                <ThumbsDown className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                        {showDeepDive && (
                          <DeepDiveButton
                            onRequest={() => onRequestDeepDive?.(msg.timestamp)}
                            loading={isRequestingDeepDive?.(msg.timestamp) ?? false}
                          />
                        )}
                      </div>
                    )}
                    {/* CEO-1: live stage progress while the dive runs, so the wait
                        is visibly moving ("Scanning the legislation… (step 2 of 5)").
                        Shown once the first stage event arrives — until then the
                        button's own "Preparing the deep dive…" label covers it. */}
                    {/* #53 dev-only: SME review under every finished answer (incl. its dive).
                        Renders only in dev builds (mode allowlist inside the component). */}
                    {msg.role === "assistant" &&
                    !msg.streaming &&
                    msg.timestamp &&
                    chatId ? (
                      <SmeFeedbackControl
                        chatId={chatId}
                        assistantTimestamp={msg.timestamp}
                        existing={msg.sme_feedback}
                      />
                    ) : null}
                    {showDeepDive &&
                    !(isRequestingDeepDive?.(msg.timestamp) ?? false) &&
                    deepDiveError?.(msg.timestamp) ? (
                      <p
                        role="alert"
                        className="px-1 text-right text-xs text-destructive"
                      >
                        {/* 08-24: the dive fails cleanly now instead of rendering a broken
                            card dump — the button above stays active for the retry. */}
                        {deepDiveError(msg.timestamp)}
                      </p>
                    ) : null}
                    {showDeepDive &&
                    (isRequestingDeepDive?.(msg.timestamp) ?? false) &&
                    deepDiveStatus?.(msg.timestamp) ? (
                      <div className="flex items-center justify-end gap-1.5 px-1 text-xs text-muted-foreground">
                        <span className="chat-loading-text">
                          {deepDiveStatus(msg.timestamp)!.replace(/[\s.…]+$/u, "")}
                        </span>
                        <span
                          className="inline-flex items-center gap-1"
                          aria-hidden="true"
                        >
                          <span className="chat-loading-dot" />
                          <span className="chat-loading-dot" />
                          <span className="chat-loading-dot" />
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </ScrollArea>
    )
  }
)
